import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, CreditCard, Calendar, Lock } from "lucide-react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { useTheme } from "../ThemeProvider";

const StripePaymentModal = ({
  isOpen,
  onClose,
  title = "Enter Card Details",
  description = "please enter your card details to proceed",
  type = "card",
  price = 0,
  frequency = "monthly",
  buttonText = "Save",
  onPaymentSuccess,
  isLoading = false
}) => {
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [cardName, setCardName] = useState("");
  const [cardNameError, setCardNameError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Stripe Elements base styling
  const stripeElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: resolvedTheme === "dark" ? "#f9fafb" : "#0f172a",
        "::placeholder": {
          color: resolvedTheme === "dark" ? "#9ca3af" : "#6b7280"
        }
      },
      invalid: {
        color: "#ef4444"
      }
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (isProcessing || isLoading) return;

    if (!stripe || !elements) {
      toast({
        title: "Stripe not loaded",
        description: "Please try again later.",
        variant: "destructive"
      });
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardName) {
      toast({
        title: "Card Holder Name",
        description: "Please enter your card holder name.",
        variant: "destructive"
      });
      return;
    }

    if (!cardElement) {
      toast({
        title: "Card Error",
        description: "Card details are missing.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: cardName,
            email: user?.email
          }
        });

      if (pmError) {
        console.error("Stripe Error:", pmError.message);
        toast({
          title: "Stripe Error",
          description:
            pmError.message ||
            "Failed to generate payment method. Please check your card details.",
          variant: "destructive"
        });
        return;
      }

      // Call the success callback with the payment method ID
      await onPaymentSuccess(paymentMethod.id);

      // Close the modal on success
      onClose();
    } catch (err) {
      console.error("Payment failed:", err);
      toast({
        title: "Payment Failed",
        description:
          "There was an error processing your payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4">
          {type === "addon" ||
            (type === "featuredPart" && (
              <div className="bg-muted/50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base font-medium">
                    Total Due
                  </span>
                  <span className="text-base sm:text-xl font-bold text-primary">
                    ${price}{" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{frequency}
                    </span>
                  </span>
                </div>
              </div>
            ))}

          <form onSubmit={handlePaymentSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cardName">Name on Card</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="cardName"
                  type="text"
                  placeholder="John Doe"
                  required
                  className={`pl-10 ${
                    cardNameError ? "border-red-500 focus:ring-red-500" : ""
                  }`}
                  value={cardName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setCardName(value);
                      setCardNameError("");
                    } else {
                      setCardNameError("Only alphabets and spaces are allowed");
                    }
                  }}
                  disabled={isProcessing || isLoading}
                />
              </div>
              {cardNameError && (
                <p className="text-sm text-red-500 mt-1">{cardNameError}</p>
              )}
            </div>

            {/* Card Number */}
            <div className="space-y-2">
              <Label htmlFor="cardNumber">Card Number</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <div className="pl-10 border rounded-md h-10 flex items-center px-3">
                  <CardNumberElement
                    id="cardNumber"
                    options={stripeElementOptions}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Expiry + CVC */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <div className="pl-10 border rounded-md h-10 flex items-center px-3">
                    <CardExpiryElement
                      id="expiryDate"
                      options={stripeElementOptions}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvc">CVC</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground " />
                  <div className="pl-10 border rounded-md h-10 flex items-center px-3">
                    <CardCvcElement
                      id="cvc"
                      options={stripeElementOptions}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isProcessing || isLoading || !stripe || !elements}
            >
              {(isProcessing || isLoading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isProcessing || isLoading ? "Processing..." : buttonText}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentModal;
